import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { ActionsPanel } from "./ActionsPanel";
import { ActivityPanel } from "./ActivityPanel";
import { CodePanel } from "./CodePanel";
import { ConnectionPanel } from "./ConnectionPanel";
import { ReviewPanel } from "./ReviewPanel";
import { VersionsPanel } from "./VersionsPanel";
import { ConnectorMonogram, ConnectorStatusBadge, MainStatus, NewVersionBadge, SourceLabel } from "./parts";
import { connectorKeys, type ConnectorTab } from "@/lib/connectors";
import { cn } from "@/lib/utils";

// …/containers/$containerId/integrations/custom/$slug: one connector Boo
// built. Review (while a version waits), the connection's variables, the
// per-action settings, approved versions, and everything that happened.
export function ConnectorPage() {
  const { orgId, containerId, slug } = useParams({ strict: false }) as {
    orgId?: string;
    containerId?: string;
    slug?: string;
  };
  const { tab } = useSearch({ strict: false }) as { tab?: ConnectorTab };
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: connectorKeys.detail(containerId ?? "", slug ?? ""),
    queryFn: () => api.connectors.get(containerId!, slug!),
    enabled: !!containerId && !!slug,
  });
  const connector = query.data;
  useDocumentTitle(connector ? connector.name : "Custom integration");

  if (!orgId || !containerId || !slug) return null;

  const hasReview = connector?.pending != null;
  const active: ConnectorTab = tab && (tab !== "review" || hasReview) ? tab : hasReview ? "review" : "connection";
  const setTab = (t: string) =>
    navigate({
      to: "/orgs/$orgId/containers/$containerId/integrations/custom/$slug",
      params: { orgId, containerId, slug },
      search: { tab: t as ConnectorTab },
      replace: true,
    });

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto">
      {/* The code view gets more room for long lines. */}
      <div className={cn("flex w-full flex-col gap-6 p-6", active === "code" ? "max-w-6xl" : "max-w-4xl")}>
        <div>
          <Button asChild size="sm" variant="ghost" className="-ml-2 gap-1.5 text-muted-foreground">
            <Link to="/orgs/$orgId/containers/$containerId/integrations" params={{ orgId, containerId }}>
              <ArrowLeft className="size-4" />
              Integrations
            </Link>
          </Button>
        </div>

        {query.isLoading ? (
          <LoadingIndicator />
        ) : query.isError ? (
          <InlineError error={query.error} />
        ) : !connector ? null : (
          <>
            <div className="flex items-center gap-3">
              <ConnectorMonogram name={connector.name} />
              <div className="min-w-0 flex-1">
                <h1 className="truncate text-lg font-semibold">{connector.name}</h1>
                <p className="truncate text-sm text-muted-foreground">
                  {connector.connection?.account ?? "Not connected yet"} · <SourceLabel source={connector.source} />
                </p>
              </div>
              {hasReview && connector.status === "live" && <NewVersionBadge />}
              <ConnectorStatusBadge status={connector.status} source={connector.source.kind} />
            </div>
            {connector.main && connector.source.kind !== "boo" && (
              <MainStatus main={connector.main} host={connector.source.kind} repoUrl={connector.source.repoUrl} />
            )}

            <Tabs value={active} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
              <TabsList className="max-w-full overflow-x-auto overflow-y-hidden">
                {hasReview && <TabsTrigger value="review">Review</TabsTrigger>}
                <TabsTrigger value="connection">Connection</TabsTrigger>
                <TabsTrigger value="actions">Actions</TabsTrigger>
                <TabsTrigger value="versions">Versions</TabsTrigger>
                <TabsTrigger value="activity">Activity</TabsTrigger>
                <TabsTrigger value="code">Code</TabsTrigger>
              </TabsList>
              {hasReview && (
                <TabsContent value="review" className="mt-4">
                  <ReviewPanel connector={connector} containerId={containerId} commit={connector.pending!.commit} />
                </TabsContent>
              )}
              <TabsContent value="connection" className="mt-4">
                <ConnectionPanel key={`${connector.running?.commit}:${connector.status}`} connector={connector} containerId={containerId} />
              </TabsContent>
              <TabsContent value="actions" className="mt-4">
                <ActionsPanel key={connector.running?.commit ?? "none"} connector={connector} containerId={containerId} />
              </TabsContent>
              <TabsContent value="versions" className="mt-4">
                <VersionsPanel connector={connector} containerId={containerId} />
              </TabsContent>
              <TabsContent value="activity" className="mt-4">
                <ActivityPanel connector={connector} containerId={containerId} />
              </TabsContent>
              <TabsContent value="code" className="mt-4">
                <CodePanel connector={connector} containerId={containerId} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}
